
import { useState, useRef, useEffect } from 'react';
import { supabase } from '../../../../lib/supabase';

export interface ReactionType {
  type: string;
  emoji: string;
  label: string;
  color: string;
  bgHover: string;
}

export const REACTION_TYPES: ReactionType[] = [
  { type: 'like', emoji: '👍', label: 'Like', color: 'text-teal-600', bgHover: 'bg-teal-50' },
  { type: 'love', emoji: '❤️', label: 'Love', color: 'text-rose-500', bgHover: 'bg-rose-50' },
  { type: 'haha', emoji: '😂', label: 'Haha', color: 'text-amber-500', bgHover: 'bg-amber-50' },
  { type: 'wow', emoji: '😮', label: 'Wow', color: 'text-amber-600', bgHover: 'bg-amber-50' },
  { type: 'sad', emoji: '😢', label: 'Sad', color: 'text-yellow-600', bgHover: 'bg-yellow-50' },
  { type: 'angry', emoji: '😡', label: 'Angry', color: 'text-orange-600', bgHover: 'bg-orange-50' },
];

interface ReactionButtonProps {
  postId: string;
  currentUserId: string | null;
  initialLikesCount: number;
}

interface ReactionCount {
  type: string;
  count: number;
}

export default function ReactionButton({
  postId,
  currentUserId,
  initialLikesCount,
}: ReactionButtonProps) {
  const [userReaction, setUserReaction] = useState<string | null>(null);
  const [reactionCounts, setReactionCounts] = useState<ReactionCount[]>([]);
  const [totalReactions, setTotalReactions] = useState(initialLikesCount);
  const [showPicker, setShowPicker] = useState(false);
  const [animatingReaction, setAnimatingReaction] = useState<string | null>(null);
  const hoverTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const leaveTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const pickerRef = useRef<HTMLDivElement>(null);

  // Load user's existing reaction and all reaction counts
  useEffect(() => {
    if (!postId) return;

    const loadReactions = async () => {
      try {
        // Get all reactions for this post
        const { data: allVotes } = await supabase
          .from('post_votes')
          .select('vote_type, user_id')
          .eq('post_id', postId);

        if (allVotes) {
          // Count by type
          const counts: Record<string, number> = {};
          let total = 0;
          allVotes.forEach((v) => {
            const t = v.vote_type || 'like';
            // Map old 'up' votes to 'like'
            const normalizedType = t === 'up' ? 'like' : t;
            counts[normalizedType] = (counts[normalizedType] || 0) + 1;
            total++;

            // Check if current user reacted
            if (currentUserId && v.user_id === currentUserId) {
              setUserReaction(normalizedType);
            }
          });

          const countArr = Object.entries(counts)
            .map(([type, count]) => ({ type, count }))
            .sort((a, b) => b.count - a.count);

          setReactionCounts(countArr);
          setTotalReactions(total);
        }
      } catch (err) {
        console.error('Error loading reactions:', err);
      }
    };

    loadReactions();
  }, [postId, currentUserId]);

  // Close picker on outside click
  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (pickerRef.current && !pickerRef.current.contains(e.target as Node)) {
        setShowPicker(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const handleReaction = async (reactionType: string) => {
    if (!currentUserId) return;

    setShowPicker(false);
    setAnimatingReaction(reactionType);
    setTimeout(() => setAnimatingReaction(null), 600);

    const previousReaction = userReaction;

    try {
      if (previousReaction === reactionType) {
        // Remove reaction
        setUserReaction(null);
        setTotalReactions((p) => Math.max(0, p - 1));
        setReactionCounts((prev) => {
          const updated = prev.map((r) =>
            r.type === reactionType ? { ...r, count: Math.max(0, r.count - 1) } : r
          ).filter((r) => r.count > 0);
          return updated;
        });

        await supabase
          .from('post_votes')
          .delete()
          .eq('post_id', postId)
          .eq('user_id', currentUserId);

        // Update post likes_count
        const newTotal = Math.max(0, totalReactions - 1);
        await supabase
          .from('posts')
          .update({ likes_count: newTotal })
          .eq('id', postId);
      } else {
        // Add or change reaction
        setUserReaction(reactionType);

        if (previousReaction) {
          // Changing reaction — total stays the same
          setReactionCounts((prev) => {
            let updated = prev.map((r) => {
              if (r.type === previousReaction) return { ...r, count: Math.max(0, r.count - 1) };
              if (r.type === reactionType) return { ...r, count: r.count + 1 };
              return r;
            }).filter((r) => r.count > 0);

            // If new type didn't exist yet
            if (!updated.find((r) => r.type === reactionType)) {
              updated.push({ type: reactionType, count: 1 });
            }
            return updated.sort((a, b) => b.count - a.count);
          });
        } else {
          // New reaction
          setTotalReactions((p) => p + 1);
          setReactionCounts((prev) => {
            const existing = prev.find((r) => r.type === reactionType);
            if (existing) {
              return prev
                .map((r) => (r.type === reactionType ? { ...r, count: r.count + 1 } : r))
                .sort((a, b) => b.count - a.count);
            }
            return [...prev, { type: reactionType, count: 1 }].sort((a, b) => b.count - a.count);
          });
        }

        await supabase
          .from('post_votes')
          .upsert(
            {
              post_id: postId,
              user_id: currentUserId,
              vote_type: reactionType,
              created_at: new Date().toISOString(),
            },
            { onConflict: 'post_id,user_id' }
          );

        if (!previousReaction) {
          const newTotal = totalReactions + 1;
          await supabase
            .from('posts')
            .update({ likes_count: newTotal })
            .eq('id', postId);
        }
      }
    } catch (err) {
      console.error('Error handling reaction:', err);
      // Revert on error
      setUserReaction(previousReaction);
    }
  };

  const handleButtonClick = () => {
    if (!currentUserId) return;
    if (userReaction) {
      // Remove current reaction
      handleReaction(userReaction);
    } else {
      // Default to 'like'
      handleReaction('like');
    }
  };

  const handleMouseEnter = () => {
    if (!currentUserId) return;
    if (leaveTimeoutRef.current) {
      clearTimeout(leaveTimeoutRef.current);
      leaveTimeoutRef.current = null;
    }
    hoverTimeoutRef.current = setTimeout(() => {
      setShowPicker(true);
    }, 400);
  };

  const handleMouseLeave = () => {
    if (hoverTimeoutRef.current) {
      clearTimeout(hoverTimeoutRef.current);
      hoverTimeoutRef.current = null;
    }
    leaveTimeoutRef.current = setTimeout(() => {
      setShowPicker(false);
    }, 300);
  };

  const activeReaction = REACTION_TYPES.find((r) => r.type === userReaction);

  // Top 3 reaction emojis for the summary
  const topReactions = reactionCounts.slice(0, 3);

  return (
    <div className="flex flex-col">
      {/* Reaction summary bar */}
      {totalReactions > 0 && (
        <div className="px-4 py-2 flex items-center justify-between text-xs text-gray-500">
          <div className="flex items-center gap-1">
            <div className="flex items-center -space-x-1">
              {topReactions.map((r) => {
                const rt = REACTION_TYPES.find((t) => t.type === r.type);
                return (
                  <span
                    key={r.type}
                    className="w-5 h-5 flex items-center justify-center rounded-full bg-white shadow-sm border border-gray-100 text-[11px]"
                    title={`${rt?.label || r.type}: ${r.count}`}
                  >
                    {rt?.emoji || '👍'}
                  </span>
                );
              })}
            </div>
            <span className="ml-1">{totalReactions}</span>
          </div>
        </div>
      )}

      {/* Action button row */}
      <div className="px-4 border-t border-gray-100">
        <div className="flex items-center">
          {/* Reaction button with hover picker */}
          <div
            className="relative flex-1"
            ref={pickerRef}
            onMouseEnter={handleMouseEnter}
            onMouseLeave={handleMouseLeave}
          >
            {/* Floating reaction picker */}
            {showPicker && (
              <div
                className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 flex items-center gap-1 bg-white rounded-full shadow-xl border border-gray-200 px-2 py-1.5 z-30"
                style={{ animation: 'reactionPickerIn 0.2s ease-out' }}
              >
                {REACTION_TYPES.map((r, i) => (
                  <button
                    key={r.type}
                    onClick={() => handleReaction(r.type)}
                    className={`w-9 h-9 flex items-center justify-center rounded-full hover:scale-125 transition-all duration-200 cursor-pointer ${
                      userReaction === r.type ? 'bg-gray-100 scale-110' : 'hover:bg-gray-50'
                    }`}
                    title={r.label}
                    style={{ animationDelay: `${i * 30}ms`, animation: 'reactionBounceIn 0.3s ease-out backwards' }}
                  >
                    <span className="text-xl">{r.emoji}</span>
                  </button>
                ))}
              </div>
            )}

            <button
              onClick={handleButtonClick}
              disabled={!currentUserId}
              className={`w-full flex items-center justify-center gap-1.5 py-2.5 text-sm font-medium transition-colors cursor-pointer rounded-lg my-0.5 ${
                activeReaction
                  ? `${activeReaction.color} ${activeReaction.bgHover}`
                  : 'text-gray-500 hover:bg-gray-50 hover:text-gray-700'
              }`}
            >
              {activeReaction ? (
                <span
                  className={`text-base ${
                    animatingReaction ? 'animate-bounce' : ''
                  }`}
                >
                  {activeReaction.emoji}
                </span>
              ) : (
                <i
                  className={`ri-heart-line text-base ${
                    animatingReaction ? 'animate-bounce' : ''
                  }`}
                ></i>
              )}
              {activeReaction ? activeReaction.label : 'Like'}
            </button>
          </div>

          {/* Comment & Share buttons are rendered by parent */}
        </div>
      </div>

      {/* Inline keyframes */}
      <style>{`
        @keyframes reactionPickerIn {
          from { opacity: 0; transform: translateX(-50%) translateY(8px) scale(0.9); }
          to { opacity: 1; transform: translateX(-50%) translateY(0) scale(1); }
        }
        @keyframes reactionBounceIn {
          0% { opacity: 0; transform: scale(0.3) translateY(10px); }
          60% { transform: scale(1.1) translateY(-2px); }
          100% { opacity: 1; transform: scale(1) translateY(0); }
        }
      `}</style>
    </div>
  );
}
